import type { SyncReceipt, VaultNote } from "./types.js";

/**
 * The vault is written by a connected AI, not by hand, so the question this
 * client has to answer is "what changed, and did it land?". These helpers
 * derive that answer from the note cache and the sync receipts.
 */

export type ActivityTone = "normal" | "attention";

export interface ActivityEntry {
  readonly id: string;
  readonly at: string;
  readonly kind: "note" | "sync";
  readonly headline: string;
  readonly detail: string;
  readonly tone: ActivityTone;
  /** Present for note entries so the feed can open the note it describes. */
  readonly noteId?: string;
}

export interface VaultPulse {
  readonly total: number;
  readonly queued: number;
  readonly conflicts: number;
  readonly arriving: number;
  /** ISO timestamp of the newest note change, absent for an empty vault. */
  readonly lastChangeAt?: string;
  /** ISO timestamp of the last successful sync, absent until one succeeds. */
  readonly lastSyncedAt?: string;
  /** The newest attempt, including failures that happened after a success. */
  readonly latestAttempt?: SyncReceipt;
}

export type VaultConnectionState = "syncing" | "failed" | "queued" | "current" | "stale" | "never-synced";

export interface SyncStatus {
  readonly state: VaultConnectionState;
  readonly buttonLabel: string;
  readonly connectionLabel: string;
}

const CURRENT_FOR_MS = 15 * 60_000;

function noteHeadline(note: VaultNote): string {
  if (note.state === "conflict") return `${note.title} needs review`;
  if (note.state === "queued") return `${note.title} is waiting to sync`;
  return `${note.title} updated`;
}

function noteDetail(note: VaultNote): string {
  if (note.state === "conflict") return `${note.path} · a remote edit and a local edit both changed this note`;
  if (note.state === "queued") return `${note.path} · not yet pushed to the hosted vault`;
  return `${note.path} · matches the hosted vault`;
}

export function noteActivity(notes: readonly VaultNote[]): readonly ActivityEntry[] {
  return notes.map((note) => ({
    id: `note:${note.id}`,
    at: note.updatedAt,
    kind: "note" as const,
    headline: noteHeadline(note),
    detail: noteDetail(note),
    tone: note.state === "conflict" ? ("attention" as const) : ("normal" as const),
    noteId: note.id,
  }));
}

export function receiptActivity(receipts: readonly SyncReceipt[]): readonly ActivityEntry[] {
  return receipts.map((receipt) => ({
    id: `sync:${receipt.id}`,
    at: receipt.at,
    kind: "sync" as const,
    headline: receipt.outcome === "synced" ? "Synced with the hosted vault" : receipt.outcome === "failed" ? "Sync did not finish" : "Sync queued",
    detail: receipt.detail,
    tone: receipt.outcome === "failed" ? ("attention" as const) : ("normal" as const),
  }));
}

/** Newest first, with notes and sync receipts interleaved by timestamp. */
export function activityFeed(
  notes: readonly VaultNote[],
  receipts: readonly SyncReceipt[],
  limit = 12,
): readonly ActivityEntry[] {
  return [...noteActivity(notes), ...receiptActivity(receipts)]
    .sort((left, right) => right.at.localeCompare(left.at) || left.id.localeCompare(right.id))
    .slice(0, Math.max(0, limit));
}

export function vaultPulse(notes: readonly VaultNote[], receipts: readonly SyncReceipt[]): VaultPulse {
  const lastSynced = receipts.filter(({ outcome }) => outcome === "synced").map(({ at }) => at).sort().at(-1);
  const lastChange = notes.map(({ updatedAt }) => updatedAt).sort().at(-1);
  const latestAttempt = [...receipts].sort((left, right) => right.at.localeCompare(left.at))[0];
  return {
    total: notes.length,
    queued: notes.filter(({ state }) => state === "queued").length,
    conflicts: notes.filter(({ state }) => state === "conflict").length,
    arriving: notes.filter((note) => lastSynced !== undefined && note.state === "synced" && note.updatedAt >= lastSynced).length,
    ...(lastChange ? { lastChangeAt: lastChange } : {}),
    ...(lastSynced ? { lastSyncedAt: lastSynced } : {}),
    ...(latestAttempt ? { latestAttempt } : {}),
  };
}

/** One line describing how fresh this browser's view of the vault is. */
export function freshnessLabel(pulse: VaultPulse, now: Date = new Date()): string {
  if (!pulse.lastSyncedAt) return "Not synced yet — press Sync to pull what your AI wrote.";
  const minutes = Math.max(0, Math.round((now.getTime() - Date.parse(pulse.lastSyncedAt)) / 60_000));
  if (!Number.isFinite(minutes)) return "Last sync time is unknown.";
  if (minutes < 1) return "Refreshed from the hosted vault just now.";
  if (minutes < 60) return `Refreshed from the hosted vault ${minutes} minute${minutes === 1 ? "" : "s"} ago.`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Refreshed from the hosted vault ${hours} hour${hours === 1 ? "" : "s"} ago.`;
  const days = Math.round(hours / 24);
  return `Refreshed from the hosted vault ${days} day${days === 1 ? "" : "s"} ago.`;
}

/**
 * The sync button's label. "Up to date" would be a claim this device cannot
 * make before its first successful sync, so a vault that has never synced says
 * so rather than looking settled.
 */
export function syncStatus(pulse: VaultPulse, syncing = false, now: Date = new Date()): SyncStatus {
  if (syncing) return { state: "syncing", buttonLabel: "Syncing…", connectionLabel: "Contacting hosted vault" };
  if (pulse.latestAttempt?.outcome === "failed") {
    return {
      state: "failed",
      buttonLabel: "Sync failed",
      connectionLabel: pulse.latestAttempt.authCode ? "Hosted vault sign-in required" : "Hosted vault unreachable",
    };
  }
  if (pulse.queued || pulse.latestAttempt?.outcome === "queued") {
    return {
      state: "queued",
      buttonLabel: pulse.queued ? `${pulse.queued} queued` : "Waiting to sync",
      connectionLabel: "Changes waiting for hosted vault",
    };
  }
  if (!pulse.lastSyncedAt) {
    return { state: "never-synced", buttonLabel: "Not synced yet", connectionLabel: "Hosted vault not checked" };
  }
  const age = now.getTime() - Date.parse(pulse.lastSyncedAt);
  if (!Number.isFinite(age) || age > CURRENT_FOR_MS) {
    return { state: "stale", buttonLabel: "Sync is stale", connectionLabel: "Hosted vault status is stale" };
  }
  return { state: "current", buttonLabel: "Up to date", connectionLabel: "Hosted vault connected" };
}

export function syncLabel(pulse: VaultPulse, syncing = false, now: Date = new Date()): string {
  return syncStatus(pulse, syncing, now).buttonLabel;
}
